import { RUNWAY_DEFAULT_BURN_DAYS } from '@nadoutong/contracts';
import type { CustomerManagerPreciseRunwayCustomer } from '@nadoutong/contracts';
import { formatUsdMicrosOrCentsAsCnyDisplay } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Panel } from '../../ui/index.ts';
import { cx } from '../../ui/cx.ts';
import { weekdayOfBusinessDate, utc8DateString } from './usageWindows.ts';

export type RunwayBurnBaseline = '1' | '3' | '7' | '30';

export const FIXED_RUNWAY_BURN_DAYS = [1, 3, 7, 30] as const;
export type FixedRunwayBurnDays = (typeof FIXED_RUNWAY_BURN_DAYS)[number];

const BURN_OPTIONS: readonly RunwayBurnBaseline[] = ['1', '3', '7', '30'];
const BURN_LABEL_KEYS: Record<RunwayBurnBaseline, I18nKey> = {
  '1': 'customer_manager_360.usage.runway.burn.1',
  '3': 'customer_manager_360.usage.runway.burn.3',
  '7': 'customer_manager_360.usage.runway.burn.7',
  '30': 'customer_manager_360.usage.runway.burn.30',
};

const ALERT_DAYS = RUNWAY_DEFAULT_BURN_DAYS;
const DAY_MS = 24 * 60 * 60 * 1000;

const WEEKDAY_KEYS = [
  'customer_manager_360.usage.weekday.0',
  'customer_manager_360.usage.weekday.1',
  'customer_manager_360.usage.weekday.2',
  'customer_manager_360.usage.weekday.3',
  'customer_manager_360.usage.weekday.4',
  'customer_manager_360.usage.weekday.5',
  'customer_manager_360.usage.weekday.6',
] as const satisfies readonly I18nKey[];

function runwayTone(daysLeft: number | null): 'ok' | 'warn' | 'bad' {
  if (daysLeft === null) return 'ok';
  if (daysLeft <= ALERT_DAYS) return 'bad';
  if (daysLeft <= 14) return 'warn';
  return 'ok';
}

function depletionDate(nowMs: number, daysLeft: number): string {
  return utc8DateString(nowMs + Math.floor(daysLeft) * DAY_MS);
}

export type RunwayFixedSample =
  | { readonly kind: 'value'; readonly daysLeft: number | null }
  | { readonly kind: 'pending' }
  | { readonly kind: 'error' };

export type RunwaySensitivity =
  | {
      readonly status: 'complete';
      readonly min: number | null;
      readonly max: number | null;
      readonly hasInfinite: boolean;
    }
  | { readonly status: 'pending' }
  | { readonly status: 'error' };

/**
 * Range of the four fixed burn windows. A subset of arrived values is
 * never a complete range: pending samples report `pending`, failed
 * samples report `error`. `daysLeft: null` is infinite runway and must
 * not enter the min as 0.
 */
export function runwaySensitivity(samples: readonly RunwayFixedSample[]): RunwaySensitivity {
  let pending = false;
  let failed = false;
  for (const sample of samples) {
    if (sample.kind === 'pending') pending = true;
    if (sample.kind === 'error') failed = true;
  }
  if (pending) return { status: 'pending' };
  if (failed) return { status: 'error' };
  let min: number | null = null;
  let max: number | null = null;
  let hasInfinite = false;
  for (const sample of samples) {
    if (sample.kind !== 'value') continue;
    if (sample.daysLeft === null) {
      hasInfinite = true;
      continue;
    }
    if (!Number.isFinite(sample.daysLeft)) continue;
    min = min === null ? sample.daysLeft : Math.min(min, sample.daysLeft);
    max = max === null ? sample.daysLeft : Math.max(max, sample.daysLeft);
  }
  return { status: 'complete', min, max, hasInfinite };
}

export function UsageRunwayPanel({
  customer,
  burn,
  onBurnChange,
  fixedDaysLeft,
  cnyPerUsd,
  nowMs,
}: {
  readonly customer: CustomerManagerPreciseRunwayCustomer | null;
  readonly burn: RunwayBurnBaseline;
  readonly onBurnChange: (next: RunwayBurnBaseline) => void;
  readonly fixedDaysLeft: readonly RunwayFixedSample[];
  readonly cnyPerUsd: number | null;
  readonly nowMs: number;
}): JSX.Element {
  const t = useT();
  const remainingMicros = customer?.remaining_usd_micros ?? 0;
  const remainingCents = customer?.remaining_usd_cents ?? 0;
  const burnPerDayMicros = customer?.burn_per_day_usd_micros ?? 0;
  const daysLeft = customer?.days_left ?? null;
  const tone = runwayTone(daysLeft);
  const baselineLabel = t(BURN_LABEL_KEYS[burn]);
  const depletes = daysLeft === null ? null : depletionDate(nowMs, daysLeft);
  const sensitivity = runwaySensitivity(fixedDaysLeft);

  return (
    <Panel
      caption={t('customer_manager_360.usage.runway.title')}
      padded
      testId="customer-360-usage-runway"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-micro text-paper-low">{t('customer_manager_360.usage.runway.burn_caption')}</span>
          <div
            role="tablist"
            aria-label={t('customer_manager_360.usage.runway.burn_aria')}
            className="inline-flex gap-1"
          >
            {BURN_OPTIONS.map((option) => {
              const active = option === burn;
              return (
                <Button
                  key={option}
                  role="tab"
                  aria-selected={active}
                  variant={active ? 'secondary' : 'ghost'}
                  size="sm"
                  className={active ? 'bg-active text-paper' : undefined}
                  data-testid={`customer-360-usage-burn-${option}`}
                  onClick={() => {
                    onBurnChange(option);
                  }}
                >
                  {t(BURN_LABEL_KEYS[option])}
                </Button>
              );
            })}
          </div>
        </div>
      }
    >
      {customer === null ? (
        <p className="m-0 text-body text-paper-low" data-testid="customer-360-usage-runway-empty">
          {t('customer_manager_360.usage.runway.empty')}
        </p>
      ) : (
        <div className="grid gap-5 md:grid-cols-[minmax(0,18rem)_1fr] md:items-center">
          <div>
            <div className="text-label text-paper-low">{t('customer_manager_360.usage.runway.days_label')}</div>
            {daysLeft === null ? (
              <p
                className="m-0 mt-1 text-section text-ok"
                data-testid="customer-360-usage-runway-days"
              >
                {t('customer_manager_360.usage.runway.infinite')}
              </p>
            ) : (
              <p
                className={cx(
                  'm-0 mt-1 font-mono text-stat-lg',
                  tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-ok',
                )}
                data-testid="customer-360-usage-runway-days"
              >
                {daysLeft.toFixed(1)}
                <span className="ml-1 text-section font-medium text-paper-mid">
                  {t('customer_manager_360.usage.runway.days_unit')}
                </span>
              </p>
            )}
            <SensitivityLine sensitivity={sensitivity} />
            {depletes !== null ? (
              <p className="m-0 mt-1 text-dense text-paper-mid">
                {t('customer_manager_360.usage.runway.depletes_on', {
                  date: depletes,
                  weekday: t(WEEKDAY_KEYS[weekdayOfBusinessDate(depletes)] ?? WEEKDAY_KEYS[0]),
                })}
              </p>
            ) : null}
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-dense">
              <dt className="text-paper-low">{t('customer_manager_360.usage.runway.remaining')}</dt>
              <dd className="m-0 font-mono text-paper">
                {formatUsdMicrosOrCentsAsCnyDisplay(remainingMicros, remainingCents, cnyPerUsd, false)}
              </dd>
              <dt className="text-paper-low">{t('customer_manager_360.usage.runway.burn_rate')}</dt>
              <dd className="m-0 font-mono text-paper" data-testid="customer-360-usage-runway-burn">
                {t('customer_manager_360.usage.runway.burn_rate_value', {
                  amount: formatUsdMicrosOrCentsAsCnyDisplay(burnPerDayMicros, Math.round(burnPerDayMicros / 10_000), cnyPerUsd, false),
                  baseline: baselineLabel,
                })}
              </dd>
              <dt className="text-paper-low">{t('customer_manager_360.usage.runway.alert_threshold')}</dt>
              <dd className="m-0 font-mono text-paper">
                {t('customer_manager_360.usage.runway.alert_days', { days: ALERT_DAYS })}
              </dd>
            </dl>
          </div>
          <RunwaySketch daysLeft={daysLeft} tone={tone} depletes={depletes} />
        </div>
      )}
      <p className="m-0 mt-3 text-micro text-paper-low">{t('customer_manager_360.usage.runway.note')}</p>
    </Panel>
  );
}

function SensitivityLine({
  sensitivity,
}: {
  readonly sensitivity: RunwaySensitivity;
}): JSX.Element | null {
  const t = useT();
  if (sensitivity.status === 'pending') {
    return (
      <p
        className="m-0 mt-1 text-dense text-paper-low"
        data-testid="customer-360-usage-runway-sensitivity-pending"
      >
        {t('customer_manager_360.usage.runway.sensitivity_pending')}
      </p>
    );
  }
  if (sensitivity.status === 'error') {
    return (
      <p
        role="alert"
        className="m-0 mt-1 text-dense text-bad"
        data-testid="customer-360-usage-runway-sensitivity-error"
      >
        {t('customer_manager_360.usage.runway.sensitivity_error')}
      </p>
    );
  }
  if (sensitivity.min === null && sensitivity.max === null && !sensitivity.hasInfinite) {
    return null;
  }
  const text =
    sensitivity.min === null || sensitivity.max === null
      ? t('customer_manager_360.usage.runway.sensitivity_only_infinite')
      : t(
          sensitivity.hasInfinite
            ? 'customer_manager_360.usage.runway.sensitivity_infinite'
            : 'customer_manager_360.usage.runway.sensitivity',
          { min: sensitivity.min.toFixed(1), max: sensitivity.max.toFixed(1) },
        );
  return (
    <p className="m-0 mt-1 text-dense text-paper-mid" data-testid="customer-360-usage-runway-sensitivity">
      {text}
    </p>
  );
}

function RunwaySketch({
  daysLeft,
  tone,
  depletes,
}: {
  readonly daysLeft: number | null;
  readonly tone: 'ok' | 'warn' | 'bad';
  readonly depletes: string | null;
}): JSX.Element {
  const t = useT();
  const width = 640;
  const height = 140;
  const padL = 8;
  const padR = 88;
  const padT = 16;
  const padB = 28;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const horizon = daysLeft === null ? 30 : Math.min(Math.max(Math.ceil(daysLeft) + 6, 14), 75);
  const zeroAt = daysLeft === null ? horizon : Math.min(daysLeft, horizon);
  const x = (day: number): number => padL + (day / horizon) * innerW;
  const stroke = tone === 'bad' ? 'stroke-bad' : tone === 'warn' ? 'stroke-warn' : 'stroke-ok';
  const fill = tone === 'bad' ? 'fill-bad' : tone === 'warn' ? 'fill-warn' : 'fill-ok';
  const alertX = daysLeft === null ? x(horizon) : x(Math.max(daysLeft - ALERT_DAYS, 0));
  const zeroX = x(zeroAt);
  const ticks: number[] = [];
  const step = Math.max(1, Math.ceil(horizon / 6));
  for (let day = 0; day <= horizon; day += step) ticks.push(day);

  return (
    <svg
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      className="h-36 w-full"
      role="img"
      data-testid="customer-360-usage-runway-chart"
    >
      {daysLeft !== null ? (
        <rect
          x={alertX}
          y={padT - 4}
          width={Math.max(zeroX - alertX, 0)}
          height={innerH + 4}
          fillOpacity={0.55}
          className="fill-bad-dim"
        />
      ) : null}
      <line x1={padL} x2={width - padR} y1={padT + innerH} y2={padT + innerH} className="stroke-line-strong" strokeWidth={1} />
      <polyline
        fill="none"
        strokeWidth={2.2}
        className={stroke}
        points={`${x(0)},${padT} ${zeroX},${padT + innerH}`}
      />
      <circle cx={x(0)} cy={padT} r={4} className={fill} />
      <circle cx={zeroX} cy={padT + innerH} r={4} className={cx('fill-canvas', stroke)} strokeWidth={2} />
      {daysLeft !== null ? (
        <>
          <text x={alertX} y={padT + 8} textAnchor="middle" className="fill-bad font-mono text-micro">
            {t('customer_manager_360.usage.runway.alert_window')}
          </text>
          {depletes !== null ? (
            <text x={zeroX + 8} y={padT + innerH - 16} className={cx('font-mono text-micro', tone === 'bad' ? 'fill-bad' : tone === 'warn' ? 'fill-warn' : 'fill-ok')}>
              {depletes}
            </text>
          ) : null}
          <text x={zeroX + 8} y={padT + innerH - 2} className="fill-paper-low text-micro">
            {t('customer_manager_360.usage.runway.depleted')}
          </text>
        </>
      ) : null}
      {ticks.map((day) => (
        <text
          key={day}
          x={x(day)}
          y={height - 8}
          textAnchor="middle"
          className="fill-paper-low font-mono text-micro"
        >
          {day === 0
            ? t('customer_manager_360.usage.runway.today')
            : t('customer_manager_360.usage.runway.plus_days', { days: day })}
        </text>
      ))}
    </svg>
  );
}
